import { SettingsNavigation } from "./settings-navigation";
import { AgentAppStack } from "@/features/agents";
import { useContext } from "react";
import React, { useState } from "react";
import { PanelLeftOpen, PanelLeftClose, Search } from "lucide-react";
import PixelAvatar from "@/shared/components/PixelAvatar";
import Logo from "@/shared/components/Logo";
import { agentMood } from "@/shared/lib/agent-mood";
import { PLANS } from "@/shared/lib/plans";
import { UpgradeButton } from "@/shared/components/UpgradeButton";
import { usePlan, useSettings } from "@/shared/api/settings";
import { useBillingSummary } from "@/shared/api/billing";
import { useUserAvatar } from "@/shared/avatar/userAvatar";
import { AgentAvatarState } from "@/shared/avatar/BuddyAvatar";
import { displayName } from "@/shared/types";
import { CreditsMeter } from "@/features/settings";
import { SearchInput } from "@/shared/components/ui-kit";
import { Tag } from "@/shared/components/teams-ui";
import { NotificationsBell } from "./NotificationsBell";
import { ThemeToggle } from "./TopBar";
import { WorkspaceNav, WORKSPACE_VIEWS } from "./WorkspaceNav";

import type { SettingsSectionId } from "@/features/settings";
import type { Agent, View } from "@/shared/types";

/* ----------------------------- user menu -------------------------------- */

// Profile pill opens workspace Settings directly — no intermediate menu.
// Notifications + theme live here too: the top bar is tabs only, so every
// piece of account chrome sits in one corner instead of two.
export function UserMenu({ collapsed, agents = [], onOpenLink }: {
  collapsed?: boolean;
  agents?: Agent[];
  /** Where a notice in the bell goes, by its path. */
  onOpenLink: (path: string) => void;
}) {
  const plan = usePlan();
  const { data: settings } = useSettings();
  const { data: billing } = useBillingSummary();
  const { seed: avatarSeed } = useUserAvatar();
  const user = settings?.user;
  const name = user ? displayName(user) : "";
  const nextPlan = PLANS[PLANS.findIndex((p) => p.id === plan.id) + 1];
  // App provides the way in; a story that renders the sidebar alone has none.
  const navigateSettings = useContext(SettingsNavigation);
  const openSettings = (s: SettingsSectionId = "general") => navigateSettings?.(s);

  return (
    <>
      {/* Plan, credits left and upgrade — sits above the profile pill. Hidden on
          mobile: the sidebar is a sheet there and the plan lives in settings.
          The meter drops out while billing loads or fails; the Billing pane
          says why. */}
      {!collapsed && (nextPlan || billing) && (
        <div className="hidden md:block mb-2 px-2.5 pt-2.5 pb-2.5 rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] shadow-[0_2px_6px_rgba(0,0,0,0.04)]">
          <span className="text-[13.5px] font-semibold text-[var(--text-primary)]">{plan.name} plan</span>
          {billing && <CreditsMeter balance={billing.balance} of={billing.plan.credits} className="mt-2" />}
          {nextPlan && (
            <UpgradeButton onClick={() => openSettings("billing")} className="mt-2.5 w-full px-0">
              Upgrade to {nextPlan.name}
            </UpgradeButton>
          )}
        </div>
      )}
      {collapsed ? (
        <div className="flex flex-col items-center gap-0.5">
          <NotificationsBell agents={agents} onOpenLink={onOpenLink} />
          <ThemeToggle />
          <button
            onClick={() => openSettings("general")}
            className="press w-9 h-9 mt-0.5 rounded-lg flex items-center justify-center hover:bg-[var(--accent-hover)]"
            title={`${name} · Settings`}
          >
            <PixelAvatar human seed={avatarSeed} hue={260} size={24} ring rounded={999} />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-0.5">
          {/* Profile pill squeezed to make room for the utility icons the top
              bar used to carry. Trailing settings glyph dropped — the whole
              pill already opens settings. */}
          <button
            onClick={() => openSettings("general")}
            title="Settings"
            className="press min-w-0 flex-1 flex items-center gap-1.5 px-1.5 py-[7px] rounded-[10px] text-left hover:bg-[var(--accent-hover)]"
          >
            <PixelAvatar human seed={avatarSeed} hue={260} size={24} ring rounded={999} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[12px] font-medium text-[var(--text-primary)] truncate tracking-[-0.005em]">
                  {name}
                </span>
                <Tag className="shrink-0">{plan.name}</Tag>
              </div>
              {/* The org the caller belongs to; the account email is the
                  fallback for someone who has no active membership. */}
              <div className="text-[11.5px] text-[var(--text-tertiary)] truncate mt-[1px]">{settings?.org?.name || user?.email}</div>
            </div>
          </button>
          <NotificationsBell agents={agents} onOpenLink={onOpenLink} />
          <ThemeToggle />
        </div>
      )}
    </>
  );
}

/* ------------------- shared sidebar building blocks ---------------------
   One source of truth for sidebar chrome — the sidebar below and its
   stories render these, so their design can't drift. */

/** Amber is not the only cue for unpublished changes: the row's tooltip says it too. */
function useAgentTitle() {
  const states = useContext(AgentAvatarState);
  return (agent: Agent) => (states.get(agent.seed)?.unpublished ? `${agent.name} · unpublished changes` : agent.name);
}

export function AgentButton({ agent, active, animate, onClick, trailing }: {
  agent: Agent;
  active?: boolean;
  /** PixelAvatar's animation name — "run", "alive", or none. */
  animate?: string | null;
  onClick: () => void;
  trailing?: React.ReactNode;
}) {
  const title = useAgentTitle();
  return (
    <button
      onClick={onClick}
      title={title(agent)}
      className={`w-full press group flex items-center gap-2 px-2 py-2.5 lg:py-1 rounded-[8px] text-left ${
        active ? "bg-[var(--accent-active)]" : "hover:bg-[var(--accent-hover)]"
      }`}
    >
      <PixelAvatar seed={agent.seed} hue={agent.hue} size={24} ring rounded={5} animate={animate} emotion={agentMood(agent)} />
      {/* Name first: the app icons ride on the same 24px line and wrap onto a
          clipped second line — dropping out whole — when the name needs the room. */}
      <div className="min-w-0 flex-1 h-6 overflow-hidden flex flex-wrap items-center gap-x-2">
        <div className="min-w-0 max-w-full h-6 flex items-center gap-1">
          <span className={`truncate text-[13.5px] ${active ? "font-medium text-[var(--text-primary)]" : "text-[var(--text-secondary)] group-hover:text-[var(--text-primary)]"}`}>
            {agent.name}
          </span>
        </div>
        <div className="ml-auto h-6 flex items-center"><AgentAppStack agent={agent} /></div>
      </div>
      {trailing}
    </button>
  );
}

/* Definable's own row: the platform's agent, drawn with the product's mark. `rail` is the
   collapsed sidebar's icon button. */
export function DefinableButton({ active, onClick, rail }: { active?: boolean; onClick: () => void; rail?: boolean }) {
  const state = active ? "bg-[var(--accent-active)]" : "hover:bg-[var(--accent-hover)]";
  if (rail) {
    return (
      <button onClick={onClick} title="Builder" className={`press w-9 h-9 rounded-lg flex items-center justify-center ${state}`}>
        <Logo size={20} className="text-[var(--text-primary)]" />
      </button>
    );
  }
  return (
    <button
      onClick={onClick}
      title="Build, test and manage your agents with Builder"
      className={`w-full press group flex items-center gap-2 px-2 py-2.5 lg:py-1 rounded-[8px] text-left ${state}`}
    >
      <span className="w-6 h-6 shrink-0 rounded-[5px] ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] flex items-center justify-center">
        <Logo size={15} className="text-[var(--text-primary)]" />
      </span>
      <span className={`truncate text-[13.5px] ${active ? "font-medium text-[var(--text-primary)]" : "text-[var(--text-secondary)] group-hover:text-[var(--text-primary)]"}`}>Builder</span>
    </button>
  );
}

/* The canvas card is inset 8px from the sidebar (`mx-2` in App.js), so a sidebar
   whose content column stops at its own right edge reads as shifted left: 8px of
   air on the left, 16px on the right. Every sidebar therefore reserves its layout
   width but draws its content 8px wider, filling that gap so both sides match.
   The card, the top-bar tab strip and the 16px text line all stay put.
   Inside a sheet there is no card, so the column stays flush with the sheet. */
export const sidebarColumn = (inSheet?: boolean) => (inSheet ? "w-[280px]" : "w-[calc(var(--sidebar-width)+8px)]");

export function SidebarSection({ label, footnote, children }: {
  label: string;
  footnote?: string;
  children?: React.ReactNode;
}) {
  return (
    <>
      <div className="px-4 pt-4 pb-1.5 section-label">{label}</div>
      <div className="px-2 space-y-[1px]">{children}</div>
      {footnote && (
        <div className="px-4 pt-1.5 text-[10px] leading-4 text-[var(--text-quaternary)]">{footnote}</div>
      )}
    </>
  );
}

// Expanded-sidebar frame: the search slot, scrollable body, UserMenu footer.
// There is no New agent: Builder makes agents, as do Templates and Duplicate.
export function SidebarFrame({ belowHeader, children, agents, onOpenLink }: {
  belowHeader?: React.ReactNode;
  children?: React.ReactNode;
  agents: Agent[];
  onOpenLink: (path: string) => void;
}) {
  return (
    <div className="w-full h-full flex flex-col fade-in">
      {belowHeader}
      <div className="flex-1 overflow-y-auto pt-2 pb-3">{children}</div>
      <div className="border-t hairline px-2 py-2">
        <UserMenu agents={agents} onOpenLink={onOpenLink} />
      </div>
    </div>
  );
}

// An agent is "running" while an agentic run still has unresolved items.
export function agentRunning(agent: Agent) {
  return agent.chats.some((c) => c.isRun && (c.pending || []).length > 0);
}

export default function LeftSidebar({
  agents, definable, view, onNav, activeAgentId, onSelectAgent, onOpenLink,
  collapsed, onToggleCollapsed, inSheet,
}: {
  agents: Agent[];
  /** Definable's row; absent until `GET /builder` answers. `active` while its view is open. */
  definable?: { active: boolean; onOpen: () => void };
  /** The open page — a workspace page lights its row, an agent page its agent. */
  view: View;
  onNav: (view: View) => void;
  activeAgentId: string;
  onSelectAgent: (agentId: string) => void;
  /** Where a notice in the bell goes, by its path: a chat, Tasks, Billing, a Spaces row. */
  onOpenLink: (path: string) => void;
  collapsed?: boolean;
  onToggleCollapsed: () => void;
  inSheet?: boolean;
}) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  // On a workspace page, or in Definable, none of your agents is the open one.
  const openAgentId = WORKSPACE_VIEWS.includes(view) || definable?.active ? null : activeAgentId;
  const shown = agents.filter((a) => a.name.toLowerCase().includes(query.toLowerCase()));
  const title = useAgentTitle();

  const agentAvatar = (a: Agent) => (
    <PixelAvatar
      seed={a.seed}
      hue={a.hue}
      size={24}
      ring
      rounded={5}
      animate={agentRunning(a) ? "run" : a.id === openAgentId ? "alive" : null}
      emotion={agentMood(a)}
    />
  );

  const collapsedContent = (
    // 64 = the 56px rail plus the card gap, so items-center lands the icons
    // midway between the window edge and the card instead of 8px left of it.
    // With many agents the rail outgrows a short screen: it scrolls, and no icon may shrink.
    <div className="w-[64px] flex-1 min-h-0 flex flex-col items-center pt-1 pb-3 gap-1 overflow-y-auto no-scrollbar [&>*]:shrink-0 fade-in">
        <button
          onClick={onToggleCollapsed}
          className="press w-9 h-9 rounded-lg hover:bg-[var(--accent-hover)] flex items-center justify-center text-[var(--text-secondary)]"
          title="Expand sidebar"
        >
          <PanelLeftOpen className="w-[14px] h-[14px]" strokeWidth={1.75} />
        </button>
        <WorkspaceNav view={view} onNav={onNav} rail />
        <div className="h-px w-6 bg-[var(--hairline)] my-1.5" />
        {/* Same order as expanded: Definable, then yours */}
        {definable && (
          <>
            <DefinableButton rail active={definable.active} onClick={definable.onOpen} />
            <div className="h-px w-6 bg-[var(--hairline)] my-1.5" />
          </>
        )}
        {agents.map((a) => (
          <button
            key={a.id}
            onClick={() => onSelectAgent(a.id)}
            className={`press w-9 h-9 rounded-lg flex items-center justify-center ${
              a.id === openAgentId ? "bg-[var(--accent-active)]" : "hover:bg-[var(--accent-hover)]"
            }`}
            title={title(a)}
          >
            {agentAvatar(a)}
          </button>
        ))}
        <div className="flex-1" />
        <UserMenu collapsed agents={agents} onOpenLink={onOpenLink} />
    </div>
  );

  const expandedContent = (
    <div className={`${sidebarColumn(inSheet)} flex-1 min-h-0`}>
    <SidebarFrame
      agents={agents}
      onOpenLink={onOpenLink}
      belowHeader={
        searchOpen && (
          <div className="px-2 pt-1 fade-in">
            <SearchInput
              raised
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search agents"
            />
          </div>
        )
      }
    >
      <div className="px-2 space-y-[1px]">
        <WorkspaceNav view={view} onNav={onNav} />
      </div>
      {definable && (
        <SidebarSection label="Definable">
          <DefinableButton active={definable.active} onClick={definable.onOpen} />
        </SidebarSection>
      )}
      <SidebarSection label="Your agents">
        {shown.map((a) => (
          <AgentButton
            key={a.id}
            agent={a}
            active={a.id === openAgentId}
            animate={agentRunning(a) ? "run" : a.id === openAgentId ? "alive" : null}
            onClick={() => onSelectAgent(a.id)}
          />
        ))}
      </SidebarSection>
    </SidebarFrame>
    </div>
  );

  return (
    <aside
      className="shrink-0 flex flex-col bg-[var(--surface-sunk)]"
      style={{ width: collapsed ? 56 : inSheet ? 280 : "var(--sidebar-width)", transition: "width .42s var(--easing)" }}
    >
      {/* The logo row. pt-4 puts the logo on the tab labels' line: the tabs sit
          8px down (the tray's margin), 4px into the tray, centred in its 40px
          strip — 32px from the top, as here. A sheet has its own menu button
          in the tray's strip, so it starts at the workspace pages. */}
      {!inSheet && (
        <div className="h-12 shrink-0 pt-4 flex items-center overflow-hidden whitespace-nowrap">
          {/* pl-5 over the rail, not pl-4: the rail centres its icons in the 64px
              column that runs to the card, so the mark has to shift the same 4px
              to stay on their axis. Expanded, pl-4 keeps it on the 16px text line. */}
          <button
            onClick={() => onNav("overview")}
            title="Overview"
            className={`press flex items-center gap-2 h-full ${collapsed ? "pl-5" : "pl-4"}`}
          >
            <Logo size={24} className="text-[var(--text-primary)] shrink-0" />
            {/* Collapsed, the wordmark also gives up its width, so the button fits the rail */}
            <span
              className={`text-[14.5px] font-semibold text-[var(--text-primary)] tracking-[-0.01em] transition-opacity duration-200 ${
                collapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100"
              }`}
            >
              Definable
            </span>
          </button>
          <div className="flex-1" />
          {/* The collapsed rail carries its own expand button. */}
          {!collapsed && (
            <div className="flex items-center gap-0.5 pr-1.5 fade-in">
              <button
                onClick={() => { setSearchOpen((v) => !v); setQuery(""); }}
                className={`press w-7 h-7 rounded-[8px] flex items-center justify-center text-[var(--text-secondary)] ${
                  searchOpen ? "bg-[var(--accent-active)]" : "hover:bg-[var(--accent-hover)]"
                }`}
                title="Search agents"
              >
                <Search className="w-[14px] h-[14px]" strokeWidth={1.75} />
              </button>
              <button
                onClick={onToggleCollapsed}
                className="press w-7 h-7 rounded-[8px] hover:bg-[var(--accent-hover)] flex items-center justify-center text-[var(--text-secondary)]"
                title="Collapse sidebar"
              >
                <PanelLeftClose className="w-[14px] h-[14px]" strokeWidth={1.75} />
              </button>
            </div>
          )}
        </div>
      )}
      {collapsed ? collapsedContent : expandedContent}
    </aside>
  );
}
